/**
 * Videocall Audio Player
 *
 * Manages Web Audio API playback: scheduling, queuing, and stopping
 * audio chunks received from the voice provider.
 *
 * All three functions share a mutable player state object.
 */

/**
 * Play an AudioBuffer at a specific scheduled time.
 * Used for gapless sequential audio chunk playback.
 *
 * @param {AudioBuffer} buffer
 * @param {number}      startTime
 * @param {object}      state  Mutable player state
 * @param {function}    state.audioCtx
 * @param {AudioBufferSourceNode|null} state.currentBufferSource
 * @param {AudioBufferSourceNode[]} state.scheduledSources
 * @param {boolean}     state.isAudioPlaying
 * @param {number}      state.pitchFactor
 * @param {function}    debugLog
 * @param {object}      callbacks
 * @param {function}    callbacks.onQueueEmpty Called when the final queued chunk ends
 */
export function playBufferScheduled(buffer, startTime, state, debugLog, callbacks = {}) {
    try {
        const source = state.audioCtx.createBufferSource();
        state.currentBufferSource = source;
        source.buffer = buffer;
        source.playbackRate.value = state.pitchFactor;
        source.connect(state.audioCtx.destination);

        state.scheduledSources.push(source);

        debugLog(`🔊 Starting playback at ${startTime.toFixed(3)} s`, {
            queueLengthAfterAdd: state.scheduledSources.length,
        });

        source.start(startTime);
        state.isAudioPlaying = true;

        source.onended = () => {
            const index = state.scheduledSources.indexOf(source);
            if (index > -1) {
                state.scheduledSources.splice(index, 1);
            }

            // Keep the speaking state active while another queued chunk remains.
            state.isAudioPlaying = state.scheduledSources.length > 0;
            debugLog("✅ Audio chunk finished", {
                queueLengthAfterRemove: state.scheduledSources.length,
                isAudioPlaying: state.isAudioPlaying,
                willNotifyQueueEmpty: !state.isAudioPlaying && typeof callbacks.onQueueEmpty === 'function',
            });
            if (!state.isAudioPlaying && callbacks.onQueueEmpty) {
                debugLog('Audio queue empty callback invoked');
                callbacks.onQueueEmpty();
            }
        };

    } catch (error) {
        console.error("❌ Error in playBufferScheduled:", error);
    }
}

/**
 * Play an AudioBuffer immediately, returning a Promise that resolves when done.
 *
 * @param {AudioBuffer} buffer
 * @param {object}      state     Mutable player state
 * @param {object}      callbacks
 * @param {function}    callbacks.onPlayStart     Called when playback begins
 * @param {function}    callbacks.onPlayEnd       Called when playback ends (after debounce)
 * @param {function}    callbacks.onPlayError     Called on error
 * @param {number}      callbacks.endDebounceMs   Debounce delay before firing onPlayEnd
 * @param {function}    debugLog
 * @returns {Promise<void>}
 */
export function playBuffer(buffer, state, callbacks, debugLog) {
    const queuedAt = Date.now();

    return new Promise((resolve, reject) => {
        try {
            const startedAt = Date.now();
            const waitTime = startedAt - queuedAt;
            debugLog(`🔊 playBuffer starting(waited ${waitTime}ms in queue): `, {
                bufferDuration: buffer.duration,
                bufferChannels: buffer.numberOfChannels,
                bufferSampleRate: buffer.sampleRate,
                audioCtxState: state.audioCtx.state,
                pitchFactor: state.pitchFactor
            });

            const source = state.audioCtx.createBufferSource();
            state.currentBufferSource = source;
            source.buffer = buffer;
            source.playbackRate.value = state.pitchFactor;
            source.connect(state.audioCtx.destination);
            debugLog("✅ Audio source created and connected to destination");

            state.isAudioPlaying = true;
            debugLog("🔊 Starting audio playback NOW...");

            if (callbacks.onPlayStart) {
                setTimeout(() => {
                    debugLog("👂 Starting interruption monitoring (after 3s delay)");
                    callbacks.onPlayStart();
                }, 3000);
            }

            let endDebounceTimeout;

            source.onended = () => {
                debugLog("✅ Audio playback ended (onended fired)");
                state.isAudioPlaying = false;

                if (endDebounceTimeout) clearTimeout(endDebounceTimeout);

                endDebounceTimeout = setTimeout(() => {
                    debugLog("🤖 AI finished speaking (after debounce)");
                    if (callbacks.onPlayEnd) callbacks.onPlayEnd();
                    resolve();
                }, callbacks.endDebounceMs || 2000);
            };

            source.onerror = (error) => {
                console.error("❌ Audio source error:", error);
                state.isAudioPlaying = false;
                if (callbacks.onPlayError) callbacks.onPlayError(error);
                reject(error);
            };

            debugLog("▶️ Calling source.start()...");
            source.start();
            debugLog("✅ source.start() called successfully");
        } catch (error) {
            console.error("❌ Error in playBuffer:", {
                error: error,
                errorName: error.name,
                errorMessage: error.message,
                stack: error.stack
            });
            state.isAudioPlaying = false;
            reject(error);
        }
    });
}

/**
 * Stop all active and scheduled audio playback.
 *
 * @param {object}       state   Mutable player state
 * @param {HTMLAudioElement|null} state.currentAudio  Legacy audio element
 * @param {function}     onStop  Optional callback after playback stops
 * @param {function}     debugLog
 */
export function stopAudio(state, onStop, debugLog) {
    // Stop HTMLAudio if assigned
    if (state.currentAudio && !state.currentAudio.paused) {
        state.currentAudio.pause();
        state.currentAudio.currentTime = 0;
        state.currentAudio = null;
    }

    // Stop current Web Audio buffer
    if (state.currentBufferSource) {
        try {
            state.currentBufferSource.onended = null;
            state.currentBufferSource.stop();
        } catch (err) {
            console.warn(err);
        }
        state.currentBufferSource = null;
    }

    // Stop ALL scheduled sources
    state.scheduledSources.forEach(source => {
        try {
            source.onended = null;
            source.stop();
        } catch (err) {
            // Source may not have started yet or already stopped
        }
    });
    state.scheduledSources = [];
    debugLog("🛑 Stopped all scheduled audio sources");

    // Clear queued promises
    state.playQueue = Promise.resolve();
    state.nextScheduledTime.val = state.audioCtx.currentTime;

    state.isAudioPlaying = false;

    if (onStop) onStop();
}
